import styled, { css, keyframes } from 'styled-components';
import Image from '../../utils/Image/Image';
import { Text12Regular, Text16Regular, Text16Bold } from '../../utils/Text/Text.styles';

export const ReelCard = styled.div`
  position: relative;
  height: 100%;
  min-height: 100%;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  overflow: hidden;
  background: #101010;
`;

const BackdropStyles = `
  height: 100%;
  aspect-ratio: unset;

  img,
  video {
    object-fit: cover;
    filter: saturate(0.95);
    transform: scale(1.02);
  }
`;

export const MediaBackdropImage = styled(Image)`
  ${BackdropStyles}
`;

export const MediaLayer = styled.div`
  position: relative;
  height: 100%;
  z-index: 0;
`;

export const MediaInteractionLayer = styled.button`
  position: absolute;
  inset: 0;
  z-index: 1;
  border: none;
  padding: 0;
  background: transparent;
  cursor: pointer;
`;

export const ReelOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: ${({ $bottomPadding }) => `1.6rem 1.2rem ${$bottomPadding || '10rem'}`};
  z-index: ${({ $zIndex }) => $zIndex || 2};
  pointer-events: none;
`;

export const TopGradient = styled.div`
  position: absolute;
  inset: 0 0 auto;
  height: 24rem;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.58) 0%, rgba(0, 0, 0, 0) 100%);
  z-index: 1;
  pointer-events: none;
`;

export const BottomGradient = styled.div`
  position: absolute;
  inset: auto 0 0;
  height: 34rem;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.78) 100%);
  z-index: 1;
  pointer-events: none;
`;

export const ReelFooter = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.2rem;
  pointer-events: none;
`;

export const ContentArea = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
`;

export const CreatorMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: min(72%, 28rem);
`;

export const NameText = styled(Text16Bold)`
  color: #fff;
`;

export const Caption = styled(Text16Regular)`
  color: #fff;
  opacity: 0.7;
  line-height: 1.45;
`;

export const ActionsColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.6rem;
  pointer-events: auto;
`;

export const AvatarButton = styled.button`
  position: relative;
  width: 3.4rem;
  height: 3.4rem;
  padding: 0;
  border: none;
  background: transparent;
  margin-bottom: 0.8rem;
`;

const AvatarBase = `
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  object-fit: cover;
`;

export const Avatar = styled.img`
  ${AvatarBase}
`;

export const AvatarFallback = styled.div`
  ${AvatarBase}
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-size: 2rem;
  font-weight: 600;
`;

export const AvatarPlusBadge = styled.span`
  position: absolute;
  left: 50%;
  bottom: -0.9rem;
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #2693ff;
  color: #fff;
  font-size: 1.2rem;
  transform: translateX(-50%);
  line-height: 1rem;
  padding-left: 0.1rem;
`;

export const ActionStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const likeIconPop = keyframes`
  0% {
    transform: scale(0.78);
  }

  32% {
    transform: scale(1.18);
  }

  58% {
    transform: scale(0.92);
  }

  100% {
    transform: scale(1);
  }
`;

const likeBurstReveal = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.35);
  }

  38% {
    opacity: 0.45;
    transform: scale(1.08);
  }

  100% {
    opacity: 0;
    transform: scale(1.55);
  }
`;

const centerLikeHeartReveal = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.32);
  }

  24% {
    opacity: 1;
    transform: scale(1.16);
  }

  48% {
    opacity: 1;
    transform: scale(0.96);
  }

  100% {
    opacity: 0;
    transform: scale(1.45);
  }
`;

const centerLikeGlowReveal = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.3);
  }

  32% {
    opacity: 0.5;
    transform: scale(1);
  }

  100% {
    opacity: 0;
    transform: scale(1.65);
  }
`;

export const ActionIconButton = styled.button`
  position: relative;
  width: 3.4rem;
  height: 3.4rem;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: ${({ $active }) => ($active ? '#ff375f' : '#fff')};
  transition:
    color 180ms ease,
    transform 180ms ease;

  &:disabled {
    cursor: default;
  }

  &:not(:disabled):active {
    transform: scale(0.94);
  }

  svg {
    width: 2.4rem;
    height: 2.4rem;
    color: inherit;
  }
`;

export const LikeIconWrap = styled.span`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  ${({ $animate }) =>
    $animate &&
    css`
      animation: ${likeIconPop} 480ms cubic-bezier(0.22, 1, 0.36, 1);
    `}
`;

export const LikeBurst = styled.span`
  position: absolute;
  inset: 0.15rem;
  border-radius: 50%;
  pointer-events: none;
  background:
    radial-gradient(circle, rgba(255, 55, 95, 0.28) 0%, rgba(255, 55, 95, 0.14) 42%, rgba(255, 55, 95, 0) 72%),
    radial-gradient(circle, rgba(255, 255, 255, 0.18) 0%, rgba(255, 255, 255, 0) 68%);
  opacity: 0;
  transform: scale(0.35);

  ${({ $animate }) =>
    $animate &&
    css`
      animation: ${likeBurstReveal} 560ms ease-out;
    `}
`;

export const CenterLikeFeedback = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 11rem;
  height: 11rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 2;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background:
      radial-gradient(circle, rgba(255, 55, 95, 0.3) 0%, rgba(255, 55, 95, 0.16) 42%, rgba(255, 55, 95, 0) 72%),
      radial-gradient(circle, rgba(255, 255, 255, 0.18) 0%, rgba(255, 255, 255, 0) 65%);
    animation: ${centerLikeGlowReveal} 760ms ease-out forwards;
  }
`;

export const CenterLikeHeart = styled.span`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  filter: drop-shadow(0 0.7rem 2rem rgba(0, 0, 0, 0.28));
  animation: ${centerLikeHeartReveal} 760ms cubic-bezier(0.22, 1, 0.36, 1) forwards;

  svg {
    width: 6.2rem;
    height: 6.2rem;
    color: inherit;
  }
`;

export const ActionCount = styled(Text12Regular)`
  color: #fff;
`;
